import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { format } from "date-fns";
import {
  setDailyData,
  setWeeklyData,
  setMonthlyData,
  setCustomData,
  setLoading,
  setError,
  setAllFloors,
  setAllZones,
  setAllDepartments,
} from "./state/reducers";
import {
  getDailyUtilization,
  getWeeklyUtilization,
  getMonthlyUtilization,
  getCustomUtilization,
} from "./services/api";
import { setAccessToken } from "./services/api/client";
import { loginRequest } from "config/authentication/azure/authConfig";
import { DateRangeSelector } from "./features/dateRangeSelector";
import { FilterControls } from "./features/filterControls";
import { SummaryStats } from "./features/summaryStats";
import { DailyHeatmap, WeeklyHeatmap, MonthlyHeatmap, CustomHeatmap } from "./features/heatmap";
import { UtilizationChart } from "./features/utilizationChart";
import { exportToExcel as exportUtil } from "./utils/exportToExcel";

const CubeDEXMetrics = () => {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();

  const { dateRangeType, customDateRange } = useSelector((state) => state.dateRange);
  const { selectedFloor, excludedDepartments, selectedZones } = useSelector(
    (state) => state.filters
  );
  const { dailyData, weeklyData, monthlyData, customData, loading } = useSelector(
    (state) => state.utilizationData
  );

  const fetchData = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      // Get access token
      const { accessToken } = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      setAccessToken(accessToken);

      const params = {
        floorName: selectedFloor,
        excludeDepartments: excludedDepartments,
        filterZones: selectedZones,
      };

      let response;
      if (dateRangeType === "daily") {
        params.date = customDateRange.from;
        response = await getDailyUtilization(params);
        dispatch(setDailyData(response.data));
      } else if (dateRangeType === "weekly") {
        params.weekStart = customDateRange.from;
        response = await getWeeklyUtilization(params);
        dispatch(setWeeklyData(response.data));
      } else if (dateRangeType === "monthly") {
        params.monthStart = customDateRange.from;
        response = await getMonthlyUtilization(params);
        dispatch(setMonthlyData(response.data));
      } else if (dateRangeType === "custom") {
        params.dateFrom = customDateRange.from;
        params.dateTo = customDateRange.to;
        response = await getCustomUtilization(params);
        dispatch(setCustomData(response.data));
      }

      // Extract metadata from response
      // Only update allFloors, allZones, allDepartments if they are not filtered
      // (i.e., when no filters are applied, to get the complete list)
      if (response?.data) {
        const floorData =
          response.data
            .find((item) => item.type === "Floors")
            ?.data?.map((f) => f.floorName) || [];
        const departmentData =
          response.data
            .find((item) => item.type === "DepartmentUtilization")
            ?.data?.map((d) => d.department) || [];
        const zoneData =
          response.data
            .find((item) => item.type === "ZoneUtilization")
            ?.data?.map((d) => d.zoneName) || [];

        // Only update metadata when no zone filter is applied (to avoid overwriting with filtered results)
        if (selectedZones.length === 0) {
          if (floorData.length > 0) dispatch(setAllFloors(floorData));
          if (departmentData.length > 0) dispatch(setAllDepartments(departmentData));
          if (zoneData.length > 0) dispatch(setAllZones(zoneData));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  }, [
    dateRangeType,
    customDateRange,
    selectedFloor,
    excludedDepartments,
    selectedZones,
    dispatch,
    instance,
    accounts,
  ]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const handleExport = () => {
    const success = exportUtil({
      utilizationData: currentData,
      dateRangeType,
      customDateRange,
    });
    if (success) {
      alert("Data exported successfully!");
    } else {
      alert("Failed to export data. Please try again.");
    }
  };

  // Get current data based on date range type
  const currentData =
    dateRangeType === "weekly"
      ? weeklyData
      : dateRangeType === "monthly"
      ? monthlyData
      : dateRangeType === "custom"
      ? customData
      : dailyData;

  // Get hourly data based on view type
  const hourlyData =
    currentData.find((item) => item.type === "HourlyUtilization")?.data || [];

  // For weekly/monthly/custom views, aggregate from nested hourlyData
  let allHourlyEntries = [];
  let chartData = [];

  if (dateRangeType === "daily") {
    allHourlyEntries = hourlyData;
    chartData = hourlyData;
  } else if (["weekly", "monthly", "custom"].includes(dateRangeType)) {
    const dataType = dateRangeType === "weekly"
      ? "WeeklyUtilization"
      : dateRangeType === "monthly"
      ? "MonthlyUtilization"
      : "CustomUtilization";
    const viewData = currentData.find((item) => item.type === dataType)?.data || [];

    // Extract all hourly entries from all days
    viewData.forEach((day) => {
      if (day.hourlyData && day.hourlyData.length > 0) {
        allHourlyEntries = allHourlyEntries.concat(day.hourlyData);
      }
    });

    // Aggregate by hour across all days for chart
    const hourlyAggregation = {};
    allHourlyEntries.forEach((entry) => {
      const hour = entry.hour;
      if (!hourlyAggregation[hour]) {
        hourlyAggregation[hour] = { count: 0, total: 0 };
      }
      hourlyAggregation[hour].count += entry.count || 0;
      hourlyAggregation[hour].total += 1;
    });

    // Convert to array format for chart
    chartData = Object.keys(hourlyAggregation)
      .map((hour) => ({
        hour: parseInt(hour),
        count: Math.round(hourlyAggregation[hour].count / hourlyAggregation[hour].total),
      }))
      .sort((a, b) => a.hour - b.hour);
  }

  // Get summary data from API
  const summaryFromApi = currentData.find((item) => item.type === "Summary")?.data;
  const floorsData = currentData.find((item) => item.type === "Floors")?.data || [];

  // Calculate capacity based on selected floor
  const totalCapacity = selectedFloor === "all"
    ? floorsData.reduce((sum, floor) => sum + (floor.capacity || 0), 0)
    : floorsData.find(floor => floor.floorName === selectedFloor)?.capacity || 0;

  const summaryData = summaryFromApi
    ? {
        uniqueUsers: summaryFromApi.uniqueUsers,
        uniqueUsersPercentage: summaryFromApi.uniqueUsersPercentage,
        totalEmployee: summaryFromApi.totalEmployee,
        averageUtilization: summaryFromApi.averageUtilization,
        averageUtilizationPercentage: summaryFromApi.averageUtilizationPercentage,
        mostUtilizedHours: summaryFromApi.mostUtilizedHours || [],
        leastUtilizedHours: summaryFromApi.leastUtilizedHours || [],
        totalCapacity,
      }
    : {
        uniqueUsers: 0,
        uniqueUsersPercentage: 0,
        totalEmployee: 0,
        averageUtilization: 0,
        averageUtilizationPercentage: 0,
        mostUtilizedHours: [],
        leastUtilizedHours: [],
        totalCapacity,
      };

  return (
    <div className="page-body">
      <div className="container-xl">

        <div className="row row-deck row-cards">
          {/* Date Range Selector */}
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <DateRangeSelector
                  onRefresh={handleRefresh}
                  onExport={handleExport}
                  loading={loading}
                  exportLoading={false}
                />
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <FilterControls />
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <SummaryStats summaryData={summaryData} loading={loading} dateRangeType={dateRangeType} />

          {/* Heatmap */}
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {dateRangeType === "daily" ? (
                  <DailyHeatmap
                    hourlyUtilizationData={hourlyData}
                    floorCapacity={summaryData.totalCapacity}
                    loading={loading}
                  />
                ) : dateRangeType === "weekly" ? (
                  <WeeklyHeatmap
                    weeklyData={currentData.find((item) => item.type === "WeeklyUtilization")?.data || []}
                    averageData={currentData.find((item) => item.type === "WeeklyAverageUtilization")?.data || []}
                    floorCapacity={summaryData.totalCapacity}
                    loading={loading}
                  />
                ) : dateRangeType === "monthly" ? (
                  <MonthlyHeatmap
                    monthlyData={currentData.find((item) => item.type === "MonthlyUtilization")?.data || []}
                    averageData={currentData.find((item) => item.type === "MonthlyAverageUtilization")?.data || []}
                    floorCapacity={summaryData.totalCapacity}
                    loading={loading}
                  />
                ) : dateRangeType === "custom" ? (
                  <CustomHeatmap
                    customData={currentData.find((item) => item.type === "CustomUtilization")?.data || []}
                    averageData={currentData.find((item) => item.type === "CustomAverageUtilization")?.data || []}
                    floorCapacity={summaryData.totalCapacity}
                    loading={loading}
                  />
                ) : (
                  <DailyHeatmap
                    hourlyUtilizationData={hourlyData}
                    floorCapacity={summaryData.totalCapacity}
                    loading={loading}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <UtilizationChart
                  hourlyUtilizationData={chartData}
                  floorCapacity={summaryData.totalCapacity}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CubeDEXMetrics;
